import * as React from 'react';
import {faTimes} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {FacetSection} from './FacetSection';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {SearchResult} from '../search.types';
import {SerializedURLBuilder, URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {SortDropdown} from './SortDropdown';
import {ToggleSection} from './ToggleSection';
import {User} from '../../users/user.class';

export interface BrowseSidebarProps {
    resultList?: SearchResult;
    serializedURLBuilder: SerializedURLBuilder;
    user: User;
}

export const BrowseSidebar = ({resultList, serializedURLBuilder, user}: BrowseSidebarProps) => {
    const urlBuilder = URLBuilder.fromSerialized(serializedURLBuilder);
    const title = `Product Categories`;

    /**
     * Template
     */
    return (
        <div
            className="tw-w-full"
            data-component="browse-sidebar-component"
            data-e2e="browse-sidebar-component"
            id="flyout"
        >
            <div className="tw-bg-none tw-box-border tw-text-sm tw-font-normal tw-leading-[1.3] tw-mb-3 tw-border tw-max-h-[calc(100vh-3rem)] tw-overflow-y-auto lg:tw-sticky lg:tw-top-0 lg:tw-z-[1024]">
                <div className="tw-flex tw-justify-between tw-items-center">
                    <div className="tw-py-2 tw-px-4">
                        <div className="tw-text-xl tw-font-normal tw-leading-[1.2] !tw-m-0">
                            <strong>Filter Results</strong>
                        </div>
                    </div>
                    <a
                        aria-label="Close Filters"
                        className="tw-py-3 tw-px-2 pointer tw-block lg:tw-hidden"
                        data-binding="closeFilterButton"
                        role="button"
                        style={{color: `inherit`}}
                        href="#"
                    >
                        <FontAwesomeIcon
                            className="tw-text-gray-700"
                            icon={faTimes}
                            size="3x"
                        />
                    </a>
                </div>
                {resultList.currentFilters?.length > 0 && resultList.totalItems > 0 && (
                    <div className="tw-mx-4 tw-py-2 tw-text-base lg:tw-hidden">
                        <a href={urlBuilder.withNoFilters()}>
                            <strong>CLEAR ALL</strong>
                        </a>
                    </div>
                )}
                <div className="tw-p-2 tw-flex tw-items-center tw-border-t lg:tw-hidden">
                    <label className="tw-whitespace-nowrap tw-mr-2 tw-mb-0">Sort By</label>
                    <SortDropdown
                        resultList={resultList}
                        user={user}
                    />
                </div>
                {resultList.subCats?.length > 0 && (
                    <div className="tw-border-t">
                        <ToggleSection title={title}>
                            <ul className="tw-m-0 !tw-p-0 tw-list-none">
                                {resultList.subCats.map((subCat, i) => (
                                    <li
                                        key={i}
                                        className="tw-text-base tw-leading-7 tw-list-none"
                                    >
                                        <a
                                            className="hover:tw-no-underline"
                                            href={urlBuilder.withWebcat(subCat.categoryPath)}
                                            onClick={() => {
                                                gaLegacyCustomEvent({
                                                    eventAction: `Category On`,
                                                    eventCategory: `Faceted Search`,
                                                    eventLabel: subCat.categoryPath,
                                                });
                                                pushGaEvent(`ga4_category_click`, {
                                                    click_action: `navigation`,
                                                    click_category_1: `filter_menu`,
                                                    click_category_2: title,
                                                    click_path: urlBuilder.withWebcat(subCat.categoryPath),
                                                    click_type: `category_click`,
                                                    selection_name: `${resultList.title} | ${subCat.name}`,
                                                });
                                            }}
                                        >
                                            {subCat.name} ({subCat.numItems})
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </ToggleSection>
                    </div>
                )}
                {resultList.facet_fields?.length > 0 &&
                    resultList.facet_fields.map((facetField, fieldIndex) => (
                        <FacetSection
                            facetField={facetField}
                            fieldIndex={fieldIndex}
                            key={fieldIndex}
                            urlBuilder={urlBuilder}
                        />
                    ))}
            </div>
        </div>
    );
};
